import { useState, useEffect, useCallback } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Filter,
  Download,
  Upload,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Shield,
  Send,
  Clock,
  GitBranch
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';

const API_URL = window.location.origin;

const TIER_BADGES = {
  direct: { label: 'Direct Partner', color: 'bg-green-500' },
  '3p': { label: '3rd Party', color: 'bg-amber-500' },
  'paid_services': { label: 'Paid Services', color: 'bg-blue-500' }
};

export const PartnerManagement = () => {
  const { token, user, isAdmin, logout } = useCMSAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [countries, setCountries] = useState([]);
  
  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Dialogs
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    university_name: '',
    country: '',
    tier: 'direct',
    notes: '',
    is_active: true
  });
  const [formLoading, setFormLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Selected partners for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchPartners = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '25' });
      if (search) params.append('search', search);
      if (tierFilter) params.append('tier', tierFilter);
      if (countryFilter) params.append('country', countryFilter);
      
      const res = await fetch(`${API_URL}/api/cms/partners?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPartners(data.partners);
        setTotalPages(data.pages);
        setTotal(data.total);
      } else if (res.status === 401) {
        toast.error('Session expired. Please log in again.');
        logout();
      }
    } catch (e) {
      toast.error('Failed to fetch partners');
    } finally {
      setLoading(false);
    }
  }, [token, page, search, tierFilter, countryFilter, logout]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/partners/stats/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      } else if (res.status === 401) {
        // Token expired, trigger re-login
        toast.error('Session expired. Please log in again.');
        logout();
      }
    } catch (e) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/partners/countries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCountries(data.countries);
      }
    } catch (e) {
      console.error('Failed to fetch countries');
    }
  };

  useEffect(() => {
    fetchPartners();
    fetchStats();
    fetchCountries();
  }, [fetchPartners]);

  const handleCreate = async () => {
    if (!formData.university_name || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/partners`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Partner created successfully');
        setAddDialogOpen(false);
        resetForm();
        fetchPartners();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create partner');
      }
    } catch (e) {
      toast.error('Failed to create partner');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPartner) return;
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/partners/${selectedPartner.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Partner updated successfully');
        setEditDialogOpen(false);
        resetForm();
        fetchPartners();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update partner');
      }
    } catch (e) {
      toast.error('Failed to update partner');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPartner) return;
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/partners/${selectedPartner.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Partner deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedPartner(null);
        fetchPartners();
        fetchStats();
      } else {
        toast.error('Failed to delete partner');
      }
    } catch (e) {
      toast.error('Failed to delete partner');
    } finally {
      setFormLoading(false);
    }
  };

  const handleBulkTierUpdate = async (newTier) => {
    if (selectedIds.length === 0) {
      toast.error('No partners selected');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/cms/partners/bulk-update-tier`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ partner_ids: selectedIds, new_tier: newTier })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Updated ${data.updated_count} partners to ${newTier}`);
        setSelectedIds([]);
        fetchPartners();
        fetchStats();
      } else {
        toast.error('Failed to update partners');
      }
    } catch (e) {
      toast.error('Failed to update partners');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error('No partners selected');
      return;
    }
    
    if (!window.confirm(`Delete ${selectedIds.length} partners?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/cms/partners/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ partner_ids: selectedIds })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Deleted ${data.deleted_count} partners`);
        setSelectedIds([]);
        fetchPartners();
        fetchStats();
      } else {
        toast.error('Failed to delete partners');
      }
    } catch (e) {
      toast.error('Failed to delete partners');
    }
  };

  const openEditDialog = (partner) => {
    setSelectedPartner(partner);
    setFormData({
      university_name: partner.university_name,
      country: partner.country,
      tier: partner.tier,
      notes: partner.notes || '',
      is_active: partner.is_active !== false
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (partner) => {
    setSelectedPartner(partner);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ university_name: '', country: '', tier: 'direct', notes: '', is_active: true });
    setSelectedPartner(null);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === partners.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(partners.map(p => p.id));
    }
  };

  // Check if user has super admin access
  const isSuperAdmin = user?.role === 'super_admin';
  const canDirectSave = user?.role === 'super_admin';

  // Submit change for approval (for non-super-admins)
  const submitForApproval = async (changeType, changes, universityId = null) => {
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/university-workflow/submit-change`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          university_id: universityId,
          change_type: changeType,
          changes: changes,
          reason: `Submitted by ${user?.name || user?.email}`
        })
      });
      
      if (res.ok) {
        toast.success('Change submitted for Super Admin approval');
        setAddDialogOpen(false);
        setEditDialogOpen(false);
        resetForm();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit for approval');
      }
    } catch (e) {
      toast.error('Failed to submit for approval');
    } finally {
      setFormLoading(false);
    }
  };

  // Modified create handler - direct save for super admin, approval for others
  const handleCreateWithApproval = async () => {
    if (!formData.university_name || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (canDirectSave) {
      await handleCreate();
    } else {
      await submitForApproval('create', formData);
    }
  };

  // Modified update handler - direct save for super admin, approval for others
  const handleUpdateWithApproval = async () => {
    if (!selectedPartner) return;
    
    if (canDirectSave) {
      await handleUpdate();
    } else {
      await submitForApproval('update', formData, selectedPartner.id);
    }
  };

  // Modified delete handler - direct delete for super admin, approval for others
  const handleDeleteWithApproval = async () => {
    if (!selectedPartner) return;
    
    if (canDirectSave) {
      await handleDelete();
    } else {
      await submitForApproval('delete', { university_name: selectedPartner.university_name }, selectedPartner.id);
    }
  };

  // Toggle status with approval workflow
  const handleToggleStatusWithApproval = async (partner) => {
    if (canDirectSave) {
      // Direct toggle
      try {
        const res = await fetch(`${API_URL}/api/cms/partners/${partner.id}/toggle-status`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          toast.success(`Partner ${partner.is_active ? 'deactivated' : 'activated'}`);
          fetchPartners();
        }
      } catch (e) {
        toast.error('Failed to toggle status');
      }
    } else {
      await submitForApproval('toggle_status', { 
        university_name: partner.university_name,
        is_active: !partner.is_active 
      }, partner.id);
    }
  };

  // Export partners to CSV (Super Admin only)
  const handleExportCSV = async () => {
    try {
      // Build query params based on current filters
      const params = new URLSearchParams();
      params.append('format', 'csv');
      if (tierFilter) params.append('tier', tierFilter);
      if (countryFilter) params.append('country', countryFilter);
      
      const res = await fetch(`${API_URL}/api/cms/partners/export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error('Export failed');
      }
      
      // Get the CSV content and download
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `partner_universities_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Partner list exported successfully');
    } catch (e) {
      console.error('Export error:', e);
      toast.error('Failed to export partner list');
    }
  };

  // Download import template
  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/partners/template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to download template');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'partner_import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  // Import partners from CSV
  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImportLoading(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/cms/partners/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Import failed');
      }
      
      setImportResult(data);
      toast.success(`Imported ${data.imported} partners`);
      fetchPartners();
      fetchStats();
    } catch (e) {
      toast.error(e.message || 'Failed to import partners');
      setImportResult({ success: false, error: e.message });
    } finally {
      setImportLoading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="partner-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ux-charcoal flex items-center gap-2">
              <Building2 className="w-7 h-7 text-ux-cyan" />
              Partner Universities
            </h1>
            <p className="text-ux-charcoal/60">
              Manage university partnerships for AI recommendations
            </p>
          </div>
          
          {isSuperAdmin && (
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={handleExportCSV}
                data-testid="export-partners-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button 
                variant="outline"
                onClick={() => setImportDialogOpen(true)}
                data-testid="import-partners-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              <Button 
                className="bg-ux-cyan hover:bg-ux-cyan-dark"
                onClick={() => { resetForm(); setAddDialogOpen(true); }}
                data-testid="add-partner-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Partner
              </Button>
            </div>
          )}
        </div>

        {/* Access Warning for Non-Super Admins */}
        {!isSuperAdmin && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-center gap-3">
              <Shield className="w-5 h-5 text-amber-500" />
              <p className="text-amber-700">
                You have view-only access. Contact a Super Admin to make changes.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-ux-charcoal/60">Total Partners</p>
              <p className="text-2xl font-bold text-ux-charcoal">{stats?.total || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-ux-charcoal/60">Direct Partners</p>
              <p className="text-2xl font-bold text-green-600">{stats?.direct || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-ux-charcoal/60">3rd Party (3P)</p>
              <p className="text-2xl font-bold text-amber-600">{stats?.third_party || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-ux-charcoal/60">Paid Services</p>
              <p className="text-2xl font-bold text-blue-600">{stats?.paid_services || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-ux-charcoal/60">Countries</p>
              <p className="text-2xl font-bold text-ux-charcoal">{countries.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ux-charcoal/40" />
                <Input
                  placeholder="Search by name or country..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={tierFilter || "all"} onValueChange={(v) => { setTierFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger className="w-[180px]" data-testid="tier-filter">
                  <SelectValue placeholder="All Tiers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="direct">Direct Partners</SelectItem>
                  <SelectItem value="3p">3rd Party (3P)</SelectItem>
                  <SelectItem value="paid_services">Paid Services</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={countryFilter || "all"} onValueChange={(v) => { setCountryFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger className="w-[180px]" data-testid="country-filter">
                  <SelectValue placeholder="All Countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Countries</SelectItem>
                  {countries.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {isSuperAdmin && selectedIds.length > 0 && (
          <Card className="border-ux-cyan bg-ux-cyan/5">
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-ux-charcoal font-medium">
                {selectedIds.length} partner(s) selected
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkTierUpdate('direct')}
                >
                  Set as Direct
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkTierUpdate('3p')}
                >
                  Set as 3P
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Selected
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Partners Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  {isSuperAdmin && (
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === partners.length && partners.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4"
                      />
                    </TableHead>
                  )}
                  <TableHead>University Name</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Last Updated</TableHead>
                  {isSuperAdmin && <TableHead className="w-16">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 6 : 4} className="text-center py-8 text-ux-charcoal/60">
                      No partners found
                    </TableCell>
                  </TableRow>
                ) : (
                  partners.map((partner) => (
                    <TableRow key={partner.id} data-testid={`partner-row-${partner.id}`}>
                      {isSuperAdmin && (
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(partner.id)}
                            onChange={() => toggleSelect(partner.id)}
                            className="w-4 h-4"
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium">{partner.university_name}</TableCell>
                      <TableCell>{partner.country}</TableCell>
                      <TableCell>
                        <Badge className={`${TIER_BADGES[partner.tier]?.color || 'bg-gray-500'} text-white`}>
                          {TIER_BADGES[partner.tier]?.label || partner.tier}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-ux-charcoal/60 text-sm">
                        {partner.updated_at 
                          ? new Date(partner.updated_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(partner)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openDeleteDialog(partner)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-ux-charcoal/60">
              Showing {((page - 1) * 25) + 1} - {Math.min(page * 25, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 py-1 text-sm">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Add Partner Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Add New Partner</DialogTitle>
              <DialogDescription>
                Add a university to the partner list for AI recommendations.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>University Name *</Label>
                <Input
                  value={formData.university_name}
                  onChange={(e) => setFormData(d => ({ ...d, university_name: e.target.value }))}
                  placeholder="e.g. University of Melbourne"
                  data-testid="university-name-input"
                />
              </div>
              <div>
                <Label>Country *</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData(d => ({ ...d, country: e.target.value }))}
                  placeholder="e.g. Australia"
                  data-testid="country-input"
                />
              </div>
              <div>
                <Label>Partner Tier *</Label>
                <Select 
                  value={formData.tier} 
                  onValueChange={(v) => setFormData(d => ({ ...d, tier: v }))}
                >
                  <SelectTrigger data-testid="tier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct Partner</SelectItem>
                    <SelectItem value="3p">3rd Party (3P)</SelectItem>
                    <SelectItem value="paid_services">Paid Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData(d => ({ ...d, notes: e.target.value }))}
                  placeholder="Any additional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateWithApproval} disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {canDirectSave ? (
                  <>Add Partner</>
                ) : (
                  <><Send className="w-4 h-4 mr-1" />Submit for Approval</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Partner Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Edit Partner</DialogTitle>
              <DialogDescription>
                Update partner university details.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>University Name *</Label>
                <Input
                  value={formData.university_name}
                  onChange={(e) => setFormData(d => ({ ...d, university_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Country *</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData(d => ({ ...d, country: e.target.value }))}
                />
              </div>
              <div>
                <Label>Partner Tier *</Label>
                <Select 
                  value={formData.tier} 
                  onValueChange={(v) => setFormData(d => ({ ...d, tier: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct Partner</SelectItem>
                    <SelectItem value="3p">3rd Party (3P)</SelectItem>
                    <SelectItem value="paid_services">Paid Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData(d => ({ ...d, notes: e.target.value }))}
                />
              </div>
              <PublishStatusCard
                isPublished={formData.is_active}
                onChange={(v) => setFormData(d => ({ ...d, is_active: v }))}
                entityLabel="partner"
                publicRoute="/partners"
                testId="partner-publish-toggle"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateWithApproval} disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {canDirectSave ? (
                  <>Save Changes</>
                ) : (
                  <><Send className="w-4 h-4 mr-1" />Submit for Approval</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete Partner
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{selectedPartner?.university_name}"? 
                {canDirectSave 
                  ? ' This action cannot be undone.'
                  : ' This will be submitted for Super Admin approval.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteWithApproval} disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {canDirectSave ? 'Delete Partner' : 'Submit Delete Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Import Partners from CSV
              </DialogTitle>
              <DialogDescription>
                Upload a CSV file to bulk import partner universities.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Template Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Download className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">Download Template</p>
                    <p className="text-sm text-blue-700 mb-2">
                      Use our template to ensure your data is formatted correctly.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDownloadTemplate}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </div>
              </div>

              {/* File Upload */}
              <div>
                <Label>Upload CSV File</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportCSV}
                    disabled={importLoading}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-ux-cyan file:text-white
                      hover:file:bg-ux-cyan-dark
                      file:cursor-pointer cursor-pointer
                      disabled:opacity-50"
                    data-testid="import-file-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  CSV columns: University Name, Country, Tier (direct/3p/paid_services), Notes
                </p>
              </div>

              {/* Import Loading */}
              {importLoading && (
                <div className="flex items-center justify-center gap-2 py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-ux-cyan" />
                  <span>Importing partners...</span>
                </div>
              )}

              {/* Import Results */}
              {importResult && (
                <div className={`rounded-lg p-4 ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {importResult.success ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">Import Complete</span>
                      </div>
                      <div className="text-sm text-green-700 space-y-1">
                        <p>✓ {importResult.imported} partners imported</p>
                        {importResult.skipped > 0 && (
                          <p>⏭ {importResult.skipped} skipped (duplicates)</p>
                        )}
                        {importResult.errors > 0 && (
                          <p>✗ {importResult.errors} errors</p>
                        )}
                      </div>
                      {importResult.details?.error_messages?.length > 0 && (
                        <div className="mt-2 text-xs text-red-600">
                          <p className="font-medium">Errors:</p>
                          {importResult.details.error_messages.map((err, i) => (
                            <p key={i}>{err}</p>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <span className="text-red-800">{importResult.error}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setImportDialogOpen(false);
                setImportResult(null);
              }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approval Workflow Info Banner (for non-super-admins) */}
        {!canDirectSave && (
          <Card className="border-yellow-300 bg-yellow-50">
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">Approval Workflow Active</p>
                <p className="text-sm text-yellow-700">
                  Changes you make will be submitted for Super Admin approval before going live.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default PartnerManagement;
